/**
 * useEditorHistory
 *
 * Undo/redo history for the PageBuilder editor state.
 * Maintains a capped stack (50 snapshots) of BlockInstance arrays
 * and exposes undo/redo with canUndo/canRedo guards.
 */
import { useState, useCallback, useRef } from 'react';
import type { BlockInstance } from './types';

const MAX_HISTORY = 50;

interface EditorSnapshot {
  blocks: BlockInstance[];
  timestamp: number;
}

interface UseEditorHistoryResult {
  blocks: BlockInstance[];
  setBlocks: (blocks: BlockInstance[], recordHistory?: boolean) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  historyLength: number;
}

export function useEditorHistory(initialBlocks: BlockInstance[]): UseEditorHistoryResult {
  const [history, setHistory] = useState<EditorSnapshot[]>([
    { blocks: initialBlocks, timestamp: Date.now() },
  ]);
  const [historyIndex, setHistoryIndex] = useState(0);

  // Keep a ref to historyIndex so redo/setBlocks callbacks always see the latest value
  // without needing it in their dependency arrays (avoids stale-closure issues).
  const historyIndexRef = useRef(historyIndex);
  const historyLengthRef = useRef(1);

  // Sync refs whenever state changes
  historyIndexRef.current = historyIndex;
  historyLengthRef.current = history.length;

  // Derived current blocks from the active snapshot
  const blocks = history[historyIndex]?.blocks ?? initialBlocks;

  const setBlocks = useCallback(
    (newBlocks: BlockInstance[], recordHistory = true) => {
      const currentIndex = historyIndexRef.current;

      if (recordHistory) {
        setHistory((prev) => {
          // Truncate any future redo branch
          const base = prev.slice(0, currentIndex + 1);
          const next = [...base, { blocks: newBlocks, timestamp: Date.now() }];
          // Cap at MAX_HISTORY — drop oldest entries
          return next.length > MAX_HISTORY
            ? next.slice(next.length - MAX_HISTORY)
            : next;
        });
        setHistoryIndex((prev) => Math.min(prev + 1, MAX_HISTORY - 1));
      } else {
        // Live typing: update current snapshot in place without branching history
        setHistory((prev) => {
          const copy = [...prev];
          copy[currentIndex] = { blocks: newBlocks, timestamp: Date.now() };
          return copy;
        });
      }
    },
    [],
  );

  const undo = useCallback(() => {
    setHistoryIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const redo = useCallback(() => {
    setHistoryIndex((prev) => Math.min(prev + 1, historyLengthRef.current - 1));
  }, []);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;
  const historyLength = history.length;

  return {
    blocks,
    setBlocks,
    undo,
    redo,
    canUndo,
    canRedo,
    historyLength,
  };
}
